import api from "../axios";
import axios from "axios";
import { formatISODate } from "../../utils/helper";
import { parseDateSafe } from "../../utils/dateFormatters";
import { BASE_URL, BASE_URL_ML } from "../../utils/constants";

const getAccountId = () => {
  return sessionStorage.getItem("acc_id");
};

const getPatientId = () => {
  return sessionStorage.getItem("_pt_id");
};

const getUser = () => {
  return JSON.parse(localStorage.getItem("user"));
};

const getLocationId = () => {
  return JSON.parse(sessionStorage.getItem("current_loc"))?.id;
};

// Patient's own location (captured at patient-detail load), falling back to the
// viewer's current location. Mirrors src/utils/helper.getPatientLocationId.
// Used as a default for the monitoring-session calls so they target the
// patient's location even if a caller omits it (SCRUM-157).
const getPatientLocationId = () => {
  return sessionStorage.getItem("_pt_loc_id") || getLocationId();
};

export async function getPatientGeneralDetails(patientId) {
  const accountId = getAccountId();
  const patient_id = patientId ?? getPatientId();
  const response = await api.get(
    `/patients/${accountId}/patients/${patient_id}/`
  );

  return response.data;
}

export async function getPatientReadings(
  patientId,
  readingType,
  timeFrame,
  dateRange
) {
  const accountId = getAccountId();

  let query;

  if (timeFrame) query = `filter=${timeFrame}`;

  if (dateRange.startDate && dateRange.endDate)
    query = `start_date=${formatISODate(
      dateRange.startDate
    )}&end_date=${formatISODate(dateRange.endDate)}`;

  if (readingType) query = `${query}&reading_type=${readingType}`;

  const response = await api.get(
    `/rpm/${accountId}/readings/${patientId}/?${query}`
  );
  return {
    readings: response.data?.readings,
    bp_stats: response.data?.bp_stats,
    stats: response?.data,
  };
}

export async function getLastDayReadings(patientId) {
  const accountId = getAccountId();
  const response = await api.get(
    `/rpm/${accountId}/readings/${patientId}/?filter=1day`
  );

  return response.data?.readings;
}

export async function getUnreviewedReadings(patientId) {
  const accountId = getAccountId();
  const response = await api.get(
    `/rpm/${accountId}/readings/${patientId}/?unreviewed_unescalated=true`
  );

  return response.data?.readings;
}

export async function createBloodPressureReading(body) {
  const accountId = getAccountId();
  const response = await api.post(`/rpm/${accountId}/reading/`, body);
  return response.data;
}

export async function createWeightReading(body) {
  const accountId = getAccountId();
  const response = await api.post(`/rpm/${accountId}/reading/`, body);
  return response.data;
}

export async function createTemperatureReading(body) {
  const accountId = getAccountId();
  const response = await api.post(`/rpm/${accountId}/reading/`, body);
  return response.data;
}

export async function createPulseOXReading(body) {
  const accountId = getAccountId();
  const response = await api.post(`/rpm/${accountId}/reading/`, body);
  return response.data;
}

export async function createGlucoseLevelReading(body) {
  const accountId = getAccountId();
  const response = await api.post(`/rpm/${accountId}/reading/`, body);
  return response.data;
}

export async function deleteReading(readingId) {
  const accountId = getAccountId();
  const response = await api.delete(`/rpm/${accountId}/reading/${readingId}/`);
  return response.data;
}

// Notes APIs
export async function getPatientNotes(
  note_type,
  sort,
  patientId,
  locationId,
  isPatient,
  // Optional history window: "1w" | "1m" | "3m" | "6m" | "1y" | "2y" | "3y".
  // Used by NotesHistoryDrawer; omitted by callers that want the full list.
  duration
) {
  if (isPatient) {
    return [];
  }

  const accountId = getAccountId();
  const response = await api.get(
    `/patients/${accountId}/${locationId}/notes/`,
    {
      params: {
        patient_id: patientId,
        sort: sort,
        ...(duration && { duration }),
        // ...(note_type !== "all" && { note_type: note_type }),
      },
    }
  );
  return response.data;
}

export async function createPatientNote(noteData, patientId) {
  const accountId = getAccountId();
  const locationId = getLocationId();
  // const patientId = getPatientId();
  noteData = {
    ...noteData,
    patient: patientId,
  };
  const response = await api.post(
    `/patients/${accountId}/${locationId}/notes/`,
    noteData
  );
  return response.data;
}

export async function updatePatientNote(
  noteId,
  noteData,
  patientId,
  locationId
) {
  const accountId = getAccountId();
  const body = {
    note_text: noteData,
    patient: patientId,
    location: locationId,
  };
  const response = await api.put(
    `/patients/${accountId}/${locationId}/notes/${noteId}/`,
    body
  );
  return response.data;
}

export async function deletePatientNote(noteId, locationId) {
  const accountId = getAccountId();
  const response = await api.delete(
    `/patients/${accountId}/${locationId}/notes/${noteId}/`
  );
  return response.data;
}

export async function getPatientCareplanNotes(patientId, locationId, sort = "latest") {
  const tenantId = getAccountId();

  const latestFlag = sort === "latest";

  const response = await api.get(
    `/ccm/${tenantId}/${locationId}/careplan-notes/`,
    {
      params: {
        patient_id: patientId,
        finalized: true,
      },
    }
  );

  return response.data ?? [];
}

// Monitoring Sessions APIs
export async function getPatientMonitoringSessions(
  patientId,
  locationId = getPatientLocationId(),
  year,
  month
) {
  const accountId = getAccountId();
  const response = await api.get(
    `/patients/${accountId}/${locationId}/monitoring-sessions/`,
    {
      params: {
        patient_id: patientId,
        // current_month: true,
        year,
        month,
      },
    }
  );
  return response.data;
}

export async function createMonitoringSession(
  sessionData,
  patientId,
  locationId = getPatientLocationId()
) {
  const accountId = getAccountId();
  const user = getUser();
  sessionData = {
    ...sessionData,
    staff_member: user?.staff_id,
    user_id: user?.auth_user_id,
    patient: patientId,
    curernt_account: user?.current_account,
  };

  const response = await api.post(
    `/patients/${accountId}/${locationId}/monitoring-sessions/`,
    sessionData
  );
  return response.data;
}

// SCRUM-850: createMonitoringSession for page close / refresh / logout. A
// keepalive fetch survives the unload (axios requests get cancelled), and
// the token is read now, before logout clears storage.
export function createMonitoringSessionOnPageClose(
  sessionData,
  patientId,
  locationId = getPatientLocationId()
) {
  const token = localStorage.getItem("authToken");
  const accountId = getAccountId();
  if (!token || !accountId || !locationId || !patientId) return;
  const user = getUser();
  try {
    fetch(`${BASE_URL}/patients/${accountId}/${locationId}/monitoring-sessions/`, {
      method: "POST",
      keepalive: true,
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        ...sessionData,
        staff_member: user?.staff_id,
        user_id: user?.auth_user_id,
        patient: patientId,
        curernt_account: user?.current_account,
      }),
    });
  } catch {
    // The page is going away; nothing useful to do with a failure here.
  }
}

export async function updateMonitoringSession(
  sessionId,
  sessionData,
  locationId = getPatientLocationId()
) {
  const accountId = getAccountId();
  const response = await api.put(
    `/patients/${accountId}/${locationId}/monitoring-sessions/${sessionId}/`,
    sessionData
  );
  return response.data;
}

export async function deleteMonitoringSession(
  sessionId,
  locationId = getPatientLocationId()
) {
  const accountId = getAccountId();
  const response = await api.delete(
    `/patients/${accountId}/${locationId}/monitoring-sessions/${sessionId}/`
  );
  return response.data;
}

export async function createPatientReadingEvent(eventData) {
  const accountId = getAccountId();   
  const patientId = getPatientId();

  const payload = {
    ...eventData,
    patient: patientId,
  };

  const response = await api.post(
    `/rpm/${accountId}/patient-reading-events/`,
    payload
  );

  return response.data;
}

export async function fetchPatientReadingEvents(readingType) {
  const accountId = getAccountId();
  const patientId = getPatientId();

  const response = await api.get(
    `/rpm/${accountId}/patient-reading-events/?patient=${patientId}&reading_type=${readingType}`,
  );

  return response.data; // return an array of events
}

// Shapes raw reading events for HealthChart / Events list. Use as the
// `select` of every fetchPatientReadingEvents query so all consumers (chart,
// PDF view) get the same `date` and `created_by` fields.
// reading_type comes back as "<type>||<created by>".
export function mapReadingEvents(data) {
  if (!Array.isArray(data)) return [];
  return data.map((event) => {
    const parts = (event.reading_type || "").split("||");
    const type = parts[0];
    const createdBy = parts.length > 1 ? parts[parts.length - 1] : "Unknown";

    return {
      ...event,
      reading_type: type,
      created_by: createdBy,
      date: event.event_date ? parseDateSafe(event.event_date) : null,
    };
  });
}

export async function deletePatientReadingEvent(eventId) {
  const accountId = getAccountId();
  const response = await api.delete(
    `/rpm/${accountId}/patient-reading-events/${eventId}/`
  );
  return response.data;
}

export async function checkPatientPhone(patientId) {
  const accountId = getAccountId();
  const response = await api.get(
    `/patients/${accountId}/phone_num_check/${patientId}/`
  );
  return response.data;
}

export async function getPatientComplianceHistory(patientId, months = 6) {
  const accountId = getAccountId();
  const patient_id = patientId ?? getPatientId();

  const response = await api.get(
    `/rpm/${accountId}/readings/${patient_id}/compliance-history/`,
    {
      params: { months },
    }
  );

  return response.data;
}

export async function getPatientMLSummary(patientId) {
  const schema_name = sessionStorage.getItem("schema_name");
  const patient_id = patientId || sessionStorage.getItem("_pt_id");

  if (!schema_name || !patient_id) {
    throw new Error("Missing required parameters: schema_name or patient_id");
  }

  const response = await axios.get(
    `${BASE_URL_ML}/chatbot/summary?db_schema=${schema_name}&patient_id=${patient_id}`
  );

  return response.data.summary;
}

/**
 * Get unreviewed readings patients for a case manager
 * GET /patients/{tenant}/{locationId}/patients/?case_manager={staff_id}&unreviewed_readings=true
 */
export async function getCaseManagerUnreviewedPatients(staffId) {
  const accountId = getAccountId();
  const locationId = getLocationId();

  if (!locationId) {
    throw new Error("Location ID not found in session storage");
  }

  const response = await api.get(
    `/patients/${accountId}/${locationId}/patients/`,
    {
      params: {
        pagination: "false", // full list (endpoint paginates by default now)
        case_manager: staffId,
        unreviewed_readings: true,
      },
    }
  );
  return response.data;
}